import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Plus, Trash2, Settings2, FolderPlus, Loader2, Check, ChevronDown, ChevronRight, Copy, LayoutTemplate, Save, Eye, FileText, FileSpreadsheet, Upload, History, Star } from "lucide-react";

const uid = () => (crypto.randomUUID ? crypto.randomUUID() : String(Math.random()).slice(2));
const cloneLines = (lines) => (lines || []).map((l) => ({ ...l, id: uid() }));
const cloneChapters = (chs) => (chs || []).map((c) => ({
  id: uid(), name: c.name,
  subchapters: (c.subchapters || []).map((s) => ({ id: uid(), name: s.name, lines: cloneLines(s.lines) })),
}));
const eur = (n) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(n || 0);
const pct = (n) => `${((n || 0) * 100).toFixed(1)}%`;
const EXT_RES = ["Sin GG", "Con GG", "SSyES"];
const TIME_TYPES = ["horas", "jornadas", "semanas", "meses"];

// Estructura base (plantilla Grabarte): Cap. 00 Desarrollo de Propuesta
// (Recursos Internos / Externos) + Módulos 01–08 con 3 subcapítulos cada uno.
const BASE_MODULES = 8;
function baseStructure() {
  const chapters = [{
    id: uid(), name: "Desarrollo de Propuesta",
    subchapters: [
      { id: uid(), name: "Recursos Internos", lines: [] },
      { id: uid(), name: "Recursos Externos", lines: [] },
    ],
  }];
  for (let m = 1; m <= BASE_MODULES; m++) {
    chapters.push({
      id: uid(), name: `Módulo ${m}`,
      subchapters: [
        { id: uid(), name: "Subcapítulo 1", lines: [] },
        { id: uid(), name: "Subcapítulo 2", lines: [] },
        { id: uid(), name: "Subcapítulo 3", lines: [] },
      ],
    });
  }
  return chapters;
}

const cell = "h-8 w-full bg-transparent border border-transparent hover:border-slate-300 rounded-md px-2 text-sm transition-colors focus:outline-none focus:border-primary focus:bg-white focus:ring-1 focus:ring-primary/20";
const cellNum = cell + " text-right tabular-nums";

function computeLine(l, cfg) {
  const rolesMap = Object.fromEntries((cfg.roles || []).map((r) => [r.name, Number(r.rate) || 0]));
  const factors = cfg.unit_factors || {};
  const internal = l.in_out === "Interno";
  const H = Number(l.ctd) || 0, J = Number(l.unidades) || 0;
  const K = internal ? (Number(factors[l.tipo]) || 0) * (rolesMap[l.recurso] || 0) : (Number(l.coste) || 0);
  const base = H * J * K;
  // Precio con margen (automático: coste + margen configurado)
  let Nauto;
  if (internal) Nauto = base;
  else if (l.recurso === "Sin GG") Nauto = base * (1 + cfg.beneficio);
  else if (l.recurso === "Con GG") Nauto = base * (1 + cfg.beneficio + cfg.gg);
  else if (l.recurso === "SSyES") Nauto = base * (1 + cfg.ssyes);
  else Nauto = base;
  // Precio cliente (override manual o, por defecto, el precio con margen)
  const hasOv = l.client_price_override !== undefined && l.client_price_override !== null && l.client_price_override !== "";
  const Ncli = hasOv ? (Number(l.client_price_override) || 0) : Nauto;
  const margins = (N) => {
    const O = internal ? N * cfg.margen_interno : 0;
    const P = internal ? 0 : base;
    const Q = N * cfg.financiero;
    const Rb = N - O - P;
    const R = Rb - Q;
    return { N, O, P, Q, Rb, R, cost: O + P + Q, S: N ? R / N : 0, Sb: N ? Rb / N : 0 };
  };
  const auto = margins(Nauto);   // precio con margen + margen previsto
  const cli = margins(Ncli);     // precio cliente + margen real
  return { K, base, Nauto, auto, ...cli };
}

function sumLines(lines, cfg) {
  return (lines || []).reduce((acc, l) => {
    const c = computeLine(l, cfg);
    acc.N += c.N; acc.cost += c.cost; acc.R += c.R; acc.O += c.O; acc.P += c.P; acc.Rb += c.Rb; acc.Q += c.Q;
    return acc;
  }, { N: 0, cost: 0, R: 0, O: 0, P: 0, Rb: 0, Q: 0 });
}

// ---- Config dialog ----
function ConfigDialog({ open, onOpenChange, cfg, onSaved }) {
  const [c, setC] = useState(() => JSON.parse(JSON.stringify(cfg)));
  const [saving, setSaving] = useState(false);
  const setParam = (k, v) => setC((p) => ({ ...p, [k]: v }));
  const setRole = (i, k, v) => setC((p) => { const roles = [...p.roles]; roles[i] = { ...roles[i], [k]: v }; return { ...p, roles }; });
  const addRole = () => setC((p) => ({ ...p, roles: [...p.roles, { name: "Nuevo rol", rate: 0 }] }));
  const delRole = (i) => setC((p) => ({ ...p, roles: p.roles.filter((_, x) => x !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        roles: c.roles.map((r) => ({ name: r.name, rate: Number(r.rate) || 0 })),
        unit_factors: {
          horas: Number(c.unit_factors.horas) || 0, jornadas: Number(c.unit_factors.jornadas) || 0,
          semanas: Number(c.unit_factors.semanas) || 0, meses: Number(c.unit_factors.meses) || 0,
        },
        areas: typeof c.areas === "string" ? c.areas.split(",").map((s) => s.trim()).filter(Boolean) : c.areas,
        gg: Number(c.gg) || 0, beneficio: Number(c.beneficio) || 0, ssyes: Number(c.ssyes) || 0,
        margen_interno: Number(c.margen_interno) || 0, financiero: Number(c.financiero) || 0,
        objetivo_empresa: c.objetivo_empresa != null ? Number(c.objetivo_empresa) : 0.15,
      };
      await api.put("/budget-config", payload);
      toast.success("Tarifas y parámetros guardados");
      onSaved(payload); onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };

  const pctField = (k, label) => (
    <div className="space-y-1"><Label className="text-xs">{label}</Label>
      <div className="relative"><Input type="number" step="0.01" value={(Number(c[k]) * 100).toFixed(2)} onChange={(e) => setParam(k, (Number(e.target.value) || 0) / 100)} className="pr-6" data-testid={`cfg-${k}`} /><span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">%</span></div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Tarifas y parámetros</DialogTitle><DialogDescription className="sr-only">Configuración global del presupuestador</DialogDescription></DialogHeader>
        <div className="space-y-5 py-2">
          <div>
            <div className="flex items-center justify-between mb-2"><Label>Tarifas por rol (coste/hora, €)</Label><Button size="sm" variant="outline" onClick={addRole} className="gap-1 h-7"><Plus className="h-3 w-3" />Rol</Button></div>
            <div className="space-y-2">
              {c.roles.map((r, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`cfg-role-${i}`}>
                  <Input value={r.name} onChange={(e) => setRole(i, "name", e.target.value)} className="flex-1" />
                  <Input type="number" value={r.rate} onChange={(e) => setRole(i, "rate", e.target.value)} className="w-24" />
                  <span className="text-xs text-muted-foreground">€/h</span>
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => delRole(i)}><Trash2 className="h-4 w-4" /></Button>
                </div>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {pctField("beneficio", "Beneficio")}
            {pctField("gg", "Gastos Generales")}
            {pctField("ssyes", "SSyES")}
            {pctField("margen_interno", "Margen interno (coste)")}
            {pctField("financiero", "Coste financiero")}
          </div>
          <div>
            <Label className="text-xs mb-1 block">Horas por unidad (jornada / semana / mes)</Label>
            <div className="grid grid-cols-4 gap-2">
              {["horas", "jornadas", "semanas", "meses"].map((k) => (
                <div key={k}><span className="text-[11px] text-muted-foreground capitalize">{k}</span><Input type="number" value={c.unit_factors[k]} onChange={(e) => setC((p) => ({ ...p, unit_factors: { ...p.unit_factors, [k]: e.target.value } }))} /></div>
              ))}
            </div>
          </div>
          <div className="space-y-1"><Label className="text-xs">Áreas (separadas por coma)</Label>
            <Input value={Array.isArray(c.areas) ? c.areas.join(", ") : c.areas} onChange={(e) => setParam("areas", e.target.value)} data-testid="cfg-areas" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={save} disabled={saving} data-testid="cfg-save-btn">{saving ? "Guardando…" : "Guardar"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---- Copiar presupuesto de otro proyecto ----
function CopyDialog({ open, onOpenChange, currentPid, onCopy }) {
  const [list, setList] = useState(null);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/budgets").then((r) => setList(r.data.filter((b) => b.project_id !== currentPid))).catch(() => setList([]));
  }, [currentPid]);
  const rows = (list || []).filter((b) => !search || b.project_name.toLowerCase().includes(search.toLowerCase()) || (b.reference || "").toLowerCase().includes(search.toLowerCase()));
  const pick = async (pid) => { setBusy(true); await onCopy(pid); setBusy(false); onOpenChange(false); };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Copiar presupuesto de otro proyecto</DialogTitle><DialogDescription className="sr-only">Selecciona un proyecto con presupuesto para copiar sus capítulos</DialogDescription></DialogHeader>
        <Input placeholder="Buscar proyecto…" value={search} onChange={(e) => setSearch(e.target.value)} className="mb-2" data-testid="copy-search" />
        <div className="max-h-80 overflow-y-auto space-y-1">
          {list === null && <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>}
          {list !== null && rows.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">No hay otros proyectos con presupuesto.</p>}
          {rows.map((b) => (
            <button key={b.project_id} disabled={busy} onClick={() => pick(b.project_id)} className="w-full text-left px-3 py-2 rounded-md hover:bg-muted transition-colors flex items-center justify-between gap-3 disabled:opacity-50" data-testid={`copy-pick-${b.project_id}`}>
              <div className="min-w-0"><div className="text-sm font-medium truncate">{b.project_name}</div>{b.reference && <div className="text-xs text-muted-foreground font-mono">{b.reference}</div>}</div>
              <span className="text-xs text-muted-foreground shrink-0">{b.chapters_count} cap.</span>
            </button>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mt-1">Los capítulos se añadirán a este presupuesto (no se borra lo existente).</p>
      </DialogContent>
    </Dialog>
  );
}

// ---- Plantillas de partidas (guardar / insertar) ----
function TemplatesDialog({ open, onOpenChange, templates, currentLines, onInsert, onSave, onDelete }) {
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!name.trim()) { toast.error("Ponle un nombre a la plantilla"); return; }
    setSaving(true); await onSave(name.trim(), currentLines); setSaving(false); setName("");
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Plantillas de partidas</DialogTitle><DialogDescription className="sr-only">Insertar o guardar bloques de partidas reutilizables</DialogDescription></DialogHeader>
        <div className="space-y-4 py-1">
          <div>
            <Label className="text-xs">Insertar una plantilla en este subcapítulo</Label>
            <div className="mt-2 max-h-56 overflow-y-auto space-y-1">
              {templates.length === 0 && <p className="text-sm text-muted-foreground py-3 text-center">Aún no tienes plantillas guardadas.</p>}
              {templates.map((t) => (
                <div key={t.id} className="flex items-center justify-between gap-2 px-2.5 py-2 rounded-md border border-border" data-testid={`tpl-row-${t.id}`}>
                  <div className="min-w-0"><div className="text-sm font-medium truncate">{t.name}</div><div className="text-xs text-muted-foreground">{(t.lines || []).length} partidas</div></div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button size="sm" variant="outline" className="h-7 gap-1" onClick={() => { onInsert(t.lines); onOpenChange(false); }} data-testid={`tpl-insert-${t.id}`}><Plus className="h-3.5 w-3.5" /> Insertar</Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={() => onDelete(t.id)} data-testid={`tpl-delete-${t.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="border-t border-border pt-3">
            <Label className="text-xs">Guardar las {currentLines.length} partidas de este subcapítulo como plantilla</Label>
            <div className="flex items-center gap-2 mt-2">
              <Input placeholder="Ej: Producción vídeo" value={name} onChange={(e) => setName(e.target.value)} data-testid="tpl-name-input" />
              <Button onClick={save} disabled={saving || currentLines.length === 0} className="gap-1.5 shrink-0" data-testid="tpl-save-btn"><Save className="h-4 w-4" /> Guardar</Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const Stat = ({ label, value }) => (
  <div className="rounded-md bg-muted/50 p-2"><div className="text-[10px] text-muted-foreground uppercase">{label}</div><div className="font-bold text-sm tabular-nums">{value}</div></div>
);

function ImportExcelDialog({ open, onOpenChange, projectId, hasCurrent, onApplied }) {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [label, setLabel] = useState("");
  const fileRef = useRef(null);
  const reset = () => { setPreview(null); setLabel(""); };

  const pick = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const r = await api.post(`/projects/${projectId}/budget/import-excel`, fd);
      setPreview(r.data);
      setLabel(f.name.replace(/\.xlsx?$/i, "").slice(0, 60));
    } catch (err) { toast.error(err?.response?.data?.detail || "No se pudo leer el Excel"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const apply = async (mode) => {
    setBusy(true);
    try {
      await api.post(`/projects/${projectId}/budget/apply-import`,
        { chapters: preview.chapters, financiero: preview.financiero, mode, label: label.trim() });
      toast.success("Presupuesto importado y activado");
      onApplied?.(); reset(); onOpenChange(false);
    } catch { toast.error("No se pudo aplicar el import"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Importar presupuesto desde Excel</DialogTitle>
          <DialogDescription>Sube el Excel del presupuesto (hoja PRESUPUESTO) y se digitaliza en capítulos y partidas.</DialogDescription>
        </DialogHeader>
        {!preview ? (
          <div className="py-8 text-center">
            <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={pick} data-testid="import-file-input" />
            <Button onClick={() => fileRef.current?.click()} disabled={busy} className="gap-2" data-testid="import-pick-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Elegir archivo .xlsx
            </Button>
            <p className="text-xs text-muted-foreground mt-3">Formato de presupuesto Grabarte (hoja "PRESUPUESTO").</p>
          </div>
        ) : (
          <div className="space-y-4 py-2" data-testid="import-preview">
            <div className="grid grid-cols-4 gap-2 text-center">
              <Stat label="Capítulos" value={preview.summary.chapters} />
              <Stat label="Subcap." value={preview.summary.subchapters} />
              <Stat label="Partidas" value={preview.summary.lines} />
              <Stat label="Total" value={eur(preview.total)} />
            </div>
            {preview.warnings?.length > 0 && (
              <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800 space-y-1">
                {preview.warnings.map((w, i) => <div key={i}>• {w}</div>)}
              </div>
            )}
            <div className="space-y-1">
              <Label className="text-xs">Nombre de la versión</Label>
              <Input value={label} onChange={(e) => setLabel(e.target.value)} data-testid="import-label-input" />
            </div>
            <p className="text-xs text-muted-foreground">
              {hasCurrent ? "El proyecto ya tiene un presupuesto. Elige qué hacer con el actual:" : "Se guardará como versión activa del presupuesto."}
            </p>
          </div>
        )}
        {preview && (
          <DialogFooter className="gap-2">
            {hasCurrent ? (
              <>
                <Button variant="outline" onClick={() => apply("overwrite")} disabled={busy} data-testid="import-overwrite-btn">Sobrescribir actual</Button>
                <Button onClick={() => apply("new_version")} disabled={busy} data-testid="import-newversion-btn">
                  {busy && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Guardar actual y crear versión
                </Button>
              </>
            ) : (
              <Button onClick={() => apply("new_version")} disabled={busy} data-testid="import-apply-btn">
                {busy && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Importar
              </Button>
            )}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

function VersionsBar({ projectId, versions, activeId, canEdit, onChanged }) {
  const [label, setLabel] = useState("");
  const [saving, setSaving] = useState(false);
  const active = versions.find((v) => v.id === activeId);
  const saveVer = async () => {
    setSaving(true);
    try { await api.post(`/projects/${projectId}/budget/versions`, { label: label.trim() }); toast.success("Versión guardada"); setLabel(""); onChanged?.(false); }
    catch { toast.error("No se pudo guardar la versión"); } finally { setSaving(false); }
  };
  const activate = async (vid) => { try { await api.post(`/projects/${projectId}/budget/versions/${vid}/activate`); toast.success("Versión activada"); onChanged?.(true); } catch { toast.error("No se pudo activar"); } };
  const del = async (vid) => { if (!window.confirm("¿Eliminar esta versión guardada?")) return; try { await api.delete(`/projects/${projectId}/budget/versions/${vid}`); onChanged?.(false); } catch { toast.error("No se pudo eliminar"); } };
  return (
    <div className="flex items-center gap-2 text-xs flex-wrap" data-testid="budget-versions-bar">
      <History className="h-3.5 w-3.5 text-muted-foreground" />
      <span className="text-muted-foreground">Versión activa:</span>
      <Badge variant="secondary" data-testid="budget-active-version">{active ? active.label : "Trabajo sin guardar"}</Badge>
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="sm" className="h-7 gap-1" data-testid="budget-versions-btn"><ChevronDown className="h-3.5 w-3.5" /> Versiones ({versions.length})</Button>
        </PopoverTrigger>
        <PopoverContent className="w-80" align="start">
          <div className="space-y-1 max-h-64 overflow-y-auto">
            {versions.length === 0 && <p className="text-xs text-muted-foreground text-center py-3">Aún no hay versiones guardadas.</p>}
            {versions.map((v) => (
              <div key={v.id} className={cn("flex items-center justify-between gap-2 rounded-md px-2 py-1.5", v.is_active ? "bg-primary/5" : "hover:bg-muted/50")} data-testid={`version-row-${v.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate flex items-center gap-1">{v.is_active && <Star className="h-3 w-3 text-gold fill-gold" />}{v.label}</div>
                  <div className="text-[10px] text-muted-foreground">{eur(v.total)} · {v.n_lines} part. · {(v.created_at || "").slice(0, 10)}</div>
                </div>
                {canEdit && (
                  <div className="flex items-center gap-1 shrink-0">
                    {!v.is_active && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => activate(v.id)} data-testid={`version-activate-${v.id}`}>Activar</Button>}
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={() => del(v.id)} data-testid={`version-del-${v.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </PopoverContent>
      </Popover>
      {canEdit && (
        <div className="flex items-center gap-1.5 ml-1">
          <Input placeholder="Guardar la actual como…" value={label} onChange={(e) => setLabel(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && label.trim() && !saving) saveVer(); }} className="h-7 w-56 text-xs" data-testid="version-label-input" />
          <Button size="sm" className="h-7 gap-1.5 text-xs" onClick={saveVer} disabled={saving || !label.trim()} data-testid="version-save-btn">{saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Guardar</Button>
        </div>
      )}
    </div>
  );
}

function SupplierCell({ line, ro, suppliers, onChange, onSupplierCreated, testid }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  const ql = q.trim().toLowerCase();
  const opts = (suppliers || []).filter((s) => !ql || (s.name || "").toLowerCase().includes(ql));
  const exact = (suppliers || []).some((s) => (s.name || "").toLowerCase() === ql);
  const label = line.supplier_name || (suppliers.find((s) => s.id === line.supplier_id) || {}).name || "— sin proveedor";

  const createNew = async () => {
    const name = q.trim();
    if (!name || creating) return;
    setCreating(true);
    try {
      const r = await api.post("/suppliers", { name });
      onSupplierCreated && onSupplierCreated(r.data);
      onChange(r.data.id, r.data.name);
      setOpen(false); setQ("");
    } catch (e) { /* sin permiso */ } finally { setCreating(false); }
  };

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setQ(""); }}>
      <PopoverTrigger asChild>
        <button type="button" disabled={ro} className={cn(cell, "text-left truncate", !line.supplier_id && !line.supplier_name && "text-muted-foreground")} data-testid={testid}>{label}</button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-64" align="start">
        <div className="p-2 border-b border-border"><input autoFocus className="w-full text-sm px-2 py-1 rounded border border-border outline-none focus:border-primary" placeholder="Buscar proveedor…" value={q} onChange={(e) => setQ(e.target.value)} data-testid={`${testid}-search`} /></div>
        <div className="max-h-60 overflow-y-auto py-1">
          <button type="button" className="w-full text-left px-3 py-1.5 text-sm hover:bg-muted flex items-center gap-2" onClick={() => { onChange("", ""); setOpen(false); }} data-testid={`${testid}-none`}>
            <Check className={cn("h-3.5 w-3.5 shrink-0", !line.supplier_id ? "opacity-100" : "opacity-0")} /> — sin proveedor
          </button>
          {opts.map((o) => (
            <button type="button" key={o.id} className="w-full text-left px-3 py-1.5 text-sm hover:bg-muted flex items-center gap-2" onClick={() => { onChange(o.id, o.name); setOpen(false); }} data-testid={`${testid}-opt-${o.id}`}>
              <Check className={cn("h-3.5 w-3.5 shrink-0", line.supplier_id === o.id ? "opacity-100" : "opacity-0")} /> <span className="truncate">{o.name}</span>
            </button>
          ))}
          {q.trim() && !exact && (
            <button type="button" className="w-full text-left px-3 py-1.5 text-sm text-primary hover:bg-muted flex items-center gap-2 border-t border-border mt-1 pt-2" onClick={createNew} disabled={creating} data-testid={`${testid}-create`}>
              <Plus className="h-3.5 w-3.5 shrink-0" /> Crear proveedor «{q.trim()}»
            </button>
          )}
          {opts.length === 0 && !q.trim() && <div className="px-3 py-2 text-xs text-muted-foreground">Escribe para buscar proveedores</div>}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function ProjectBudget({ projectId, mode = "manage", onReset }) {
  const ro = mode === "view";           // solo lectura
  const canGlobal = mode === "manage";  // Admin/Directora: copiar, tarifas, plantillas
  const [cfg, setCfg] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [financiero, setFinanciero] = useState(0.03);   // coste financiero por proyecto (def. 3%)
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saveState, setSaveState] = useState("saved"); // saved | saving | dirty
  const [showConfig, setShowConfig] = useState(false);
  const [collapsed, setCollapsed] = useState({});
  const [templates, setTemplates] = useState([]);
  const [showCopy, setShowCopy] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [versions, setVersions] = useState([]);
  const [activeVerId, setActiveVerId] = useState(null);
  const [tplTarget, setTplTarget] = useState(null); // {ci, si}
  const loadedRef = useRef(false);
  const timerRef = useRef(null);

  useEffect(() => {
    let alive = true;
    const loaders = [api.get("/budget-config"), api.get(`/projects/${projectId}/budget`)];
    if (canGlobal) loaders.push(api.get("/budget-templates"));
    Promise.all(loaders)
      .then((res) => {
        if (!alive) return;
        setCfg(res[0].data);
        setTemplates(res[2]?.data || []);
        const bdoc = res[1].data;
        setFinanciero(bdoc.financiero != null ? bdoc.financiero : (res[0].data.financiero ?? 0.03));
        let chs = bdoc.chapters || [];
        if (!ro && chs.length === 0) {
          // Presupuesto nuevo → aparece ya montado con la estructura base
          chs = baseStructure();
          api.put(`/projects/${projectId}/budget`, { chapters: chs }).catch(() => {});
        }
        setChapters(chs);
        setLoading(false);
        setTimeout(() => { loadedRef.current = true; }, 100);
      })
      .catch(() => { if (alive) { toast.error("No se pudo cargar el presupuesto"); setLoading(false); } });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const loadVersions = () => api.get(`/projects/${projectId}/budget/versions`)
    .then((r) => { setVersions(r.data.versions || []); setActiveVerId(r.data.active_version_id || null); })
    .catch(() => {});
  useEffect(() => { if (!ro) loadVersions(); /* eslint-disable-next-line */ }, [projectId]);

  useEffect(() => { api.get("/suppliers").then((r) => setSuppliers(r.data || [])).catch(() => {}); }, []);

  const reloadBudget = async () => {
    const r = await api.get(`/projects/${projectId}/budget`);
    loadedRef.current = false;
    setChapters(r.data.chapters || []);
    setFinanciero(r.data.financiero != null ? r.data.financiero : (cfg?.financiero ?? 0.03));
    setTimeout(() => { loadedRef.current = true; }, 250);
  };
  const onVersionsChanged = (reload) => { loadVersions(); if (reload) reloadBudget(); };

  // Debounced autosave
  useEffect(() => {
    if (!loadedRef.current || ro) return;
    setSaveState("dirty");
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      setSaveState("saving");
      try { await api.put(`/projects/${projectId}/budget`, { chapters, financiero }); setSaveState("saved"); }
      catch (e) { setSaveState("dirty"); toast.error("Error al guardar"); }
    }, 1200);
    return () => clearTimeout(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chapters, financiero]);

  const mut = (fn) => setChapters((prev) => fn(JSON.parse(JSON.stringify(prev))));
  const addChapter = () => mut((ch) => [...ch, { id: uid(), name: "Nuevo capítulo", subchapters: [{ id: uid(), name: "Subcapítulo 1", lines: [] }] }]);
  const delChapter = (ci) => { if (window.confirm("¿Eliminar capítulo?")) mut((ch) => ch.filter((_, i) => i !== ci)); };
  const setChapter = (ci, patch) => mut((ch) => { ch[ci] = { ...ch[ci], ...patch }; return ch; });
  const addSub = (ci) => mut((ch) => { ch[ci].subchapters.push({ id: uid(), name: "Nuevo subcapítulo", lines: [] }); return ch; });
  const delSub = (ci, si) => { if (window.confirm("¿Eliminar subcapítulo?")) mut((ch) => { ch[ci].subchapters.splice(si, 1); return ch; }); };
  const setSub = (ci, si, patch) => mut((ch) => { ch[ci].subchapters[si] = { ...ch[ci].subchapters[si], ...patch }; return ch; });
  const addLine = (ci, si) => mut((ch) => {
    ch[ci].subchapters[si].lines.push({ id: uid(), concepto: "", area: (cfg.areas || [])[0] || "", in_out: "Interno", recurso: (cfg.roles || [])[0]?.name || "", ctd: 1, tipo: "horas", unidades: 0, coste: 0, supplier_id: "", comentarios: "" });
    return ch;
  });
  const delLine = (ci, si, li) => mut((ch) => { ch[ci].subchapters[si].lines.splice(li, 1); return ch; });
  const setLine = (ci, si, li, patch) => mut((ch) => {
    const line = { ...ch[ci].subchapters[si].lines[li], ...patch };
    if (patch.in_out) {
      if (patch.in_out === "Interno") { line.recurso = (cfg.roles || [])[0]?.name || ""; line.tipo = TIME_TYPES.includes(line.tipo) ? line.tipo : "horas"; line.coste = 0; }
      else { line.recurso = EXT_RES[0]; line.tipo = "t/a"; }
    }
    ch[ci].subchapters[si].lines[li] = line;
    return ch;
  });

  const refreshTemplates = async () => { try { const r = await api.get("/budget-templates"); setTemplates(r.data || []); } catch (e) {} };
  const insertTemplate = (ci, si, lines) => { mut((ch) => { ch[ci].subchapters[si].lines.push(...cloneLines(lines)); return ch; }); toast.success("Plantilla insertada"); };
  const saveTemplate = async (name, lines) => {
    try { const stripped = lines.map(({ id, ...rest }) => rest); await api.post("/budget-templates", { name, lines: stripped }); toast.success("Plantilla guardada"); refreshTemplates(); }
    catch (e) { toast.error("Error al guardar la plantilla"); }
  };
  const deleteTemplate = async (id) => { try { await api.delete(`/budget-templates/${id}`); refreshTemplates(); } catch (e) { toast.error("Error"); } };
  const copyFromProject = async (pid) => {
    try {
      const { data } = await api.get(`/projects/${pid}/budget`);
      const chs = cloneChapters(data.chapters || []);
      if (!chs.length) { toast.error("Ese proyecto no tiene capítulos"); return; }
      mut((ch) => [...ch, ...chs]);
      toast.success(`Copiados ${chs.length} capítulos`);
    } catch (e) { toast.error("No se pudo copiar el presupuesto"); }
  };
  const loadBaseTemplate = () => { mut(() => baseStructure()); toast.success("Estructura base cargada"); };

  const deleteBudget = async () => {
    if (!window.confirm("¿Eliminar el presupuesto completo de este proyecto? Se borrarán todos los capítulos y versiones. Podrás elegir otra plantilla.")) return;
    try {
      await api.delete(`/projects/${projectId}/budget`);
      toast.success("Presupuesto eliminado");
      onReset ? onReset() : setChapters([]);
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo eliminar el presupuesto"); }
  };

  if (loading || !cfg) return <div className="flex justify-center py-16"><div className="h-7 w-7 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>;

  const effCfg = { ...cfg, financiero };
  const grand = chapters.reduce((acc, c) => {
    c.subchapters.forEach((s) => { const t = sumLines(s.lines, effCfg); acc.N += t.N; acc.cost += t.cost; acc.R += t.R; acc.O += t.O; acc.P += t.P; acc.Rb += t.Rb; acc.Q += t.Q; });
    return acc;
  }, { N: 0, cost: 0, R: 0, O: 0, P: 0, Rb: 0, Q: 0 });

  return (
    <div className="space-y-5">
      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <Card className="border-border shadow-none rounded-xl p-4"><div className="text-[11px] text-muted-foreground leading-tight">Total (precio cliente)</div><div className="font-heading font-bold text-xl tabular-nums whitespace-nowrap mt-1.5 text-navy" data-testid="budget-total-price">{eur(grand.N)}</div></Card>
        <Card className="border-border shadow-none rounded-xl p-4"><div className="text-[11px] text-muted-foreground leading-tight">Coste en horas</div><div className="font-heading font-bold text-xl tabular-nums whitespace-nowrap mt-1.5" data-testid="budget-cost-hours">{eur(grand.O)}</div></Card>
        <Card className="border-border shadow-none rounded-xl p-4"><div className="text-[11px] text-muted-foreground leading-tight">Costes externos</div><div className="font-heading font-bold text-xl tabular-nums whitespace-nowrap mt-1.5" data-testid="budget-cost-external">{eur(grand.P)}</div></Card>
        <Card className="border-border shadow-none rounded-xl p-4"><div className="text-[11px] text-muted-foreground leading-tight">Margen bruto</div><div className={cn("font-heading font-bold text-xl tabular-nums whitespace-nowrap mt-1.5", grand.Rb >= 0 ? "text-foreground" : "text-destructive")} data-testid="budget-margin-gross">{eur(grand.Rb)}</div><div className="text-[11px] text-muted-foreground mt-0.5">{grand.N ? pct(grand.Rb / grand.N) : "—"}</div></Card>
        <Card className="border-border shadow-none rounded-xl p-4 bg-primary/[0.04]"><div className="text-[11px] text-muted-foreground leading-tight">Margen neto</div><div className={cn("font-heading font-bold text-xl tabular-nums whitespace-nowrap mt-1.5", grand.R >= 0 ? "text-primary" : "text-destructive")} data-testid="budget-margin-net">{eur(grand.R)}</div><div className="text-[11px] text-muted-foreground mt-0.5">{grand.N ? pct(grand.R / grand.N) : "—"} · −{eur(grand.Q)} coste fin. ({pct(financiero)})</div><span className="sr-only" data-testid="budget-financiero-note">coste financiero</span></Card>
      </div>

      {/* Barra de acciones */}
      <div className="flex flex-wrap items-center gap-2">
        {!ro && (
          <div className="flex items-center gap-1.5" data-testid="budget-financiero-control">
            <Label className="text-[11px] text-muted-foreground whitespace-nowrap">Coste fin.</Label>
            <div className="relative w-[74px]">
              <Input type="number" step="0.5" min="0" value={Number((financiero * 100).toFixed(2))}
                onChange={(e) => setFinanciero(Math.max(0, (Number(e.target.value) || 0)) / 100)}
                className="h-8 pr-5 text-sm tabular-nums" data-testid="budget-financiero-input" />
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">%</span>
            </div>
          </div>
        )}
        {ro && <span className="text-xs text-muted-foreground flex items-center gap-1" data-testid="budget-readonly"><Eye className="h-3 w-3" /> Solo lectura</span>}
        {!ro && <span className="text-xs text-muted-foreground flex items-center gap-1" data-testid="budget-save-state">
          {saveState === "saving" ? <><Loader2 className="h-3 w-3 animate-spin" /> Guardando…</> : saveState === "dirty" ? "Cambios sin guardar" : <><Check className="h-3 w-3 text-primary" /> Guardado</>}
        </span>}
        <div className="flex-1 min-w-0" />
        {canGlobal && <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setShowCopy(true)} data-testid="budget-copy-btn"><Copy className="h-4 w-4" /> Copiar</Button>}
        {!ro && <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setShowImport(true)} data-testid="budget-import-btn"><FileSpreadsheet className="h-4 w-4" /> Importar Excel</Button>}
        {canGlobal && <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setShowConfig(true)} data-testid="budget-config-btn"><Settings2 className="h-4 w-4" /> Tarifas</Button>}
        {canGlobal && <Button variant="outline" size="sm" className="gap-1.5 text-destructive hover:text-destructive" onClick={deleteBudget} data-testid="budget-delete-btn"><Trash2 className="h-4 w-4" /> Eliminar presupuesto</Button>}
        <Button size="sm" className="gap-1.5" onClick={() => navigate(`/proyecto/${projectId}/propuesta`)} data-testid="budget-proposal-btn"><FileText className="h-4 w-4" /> Propuesta cliente</Button>
      </div>

      {!ro && (
        <VersionsBar projectId={projectId} versions={versions} activeId={activeVerId} canEdit={!ro} onChanged={onVersionsChanged} />
      )}

      {chapters.length === 0 && (
        <Card className="border-border border-dashed shadow-none py-14 text-center">
          <p className="text-sm text-muted-foreground mb-3">Este proyecto aún no tiene presupuesto.</p>
          {!ro && (
            <div className="flex flex-wrap items-center justify-center gap-2">
              <Button onClick={loadBaseTemplate} className="gap-1.5" data-testid="budget-use-base"><LayoutTemplate className="h-4 w-4" /> Usar plantilla base</Button>
              <Button variant="outline" onClick={addChapter} className="gap-1.5" data-testid="budget-add-first"><Plus className="h-4 w-4" /> Empezar en blanco</Button>
            </div>
          )}
        </Card>
      )}

      <fieldset disabled={ro} className="contents">

      {chapters.map((ch, ci) => {
        const chTot = ch.subchapters.reduce((a, s) => { const t = sumLines(s.lines, effCfg); a.N += t.N; a.cost += t.cost; a.R += t.R; return a; }, { N: 0, cost: 0, R: 0 });
        const isOpen = !collapsed[ch.id];
        return (
          <Card key={ch.id} className="border-border shadow-none overflow-hidden" data-testid={`budget-chapter-${ci}`}>
            <div className="flex items-center gap-2.5 bg-navy/[0.05] px-3 py-3 border-b border-border">
              <button onClick={() => setCollapsed((p) => ({ ...p, [ch.id]: !p[ch.id] }))} className="text-muted-foreground shrink-0">{isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</button>
              <span className="text-[11px] font-mono font-semibold text-white bg-navy/85 rounded px-1.5 py-0.5 shrink-0">{String(ci + 1).padStart(2, "0")}</span>
              <input className="flex-1 bg-transparent font-heading font-bold text-base focus:outline-none focus:bg-white rounded px-1.5 py-0.5" value={ch.name} onChange={(e) => setChapter(ci, { name: e.target.value })} data-testid={`chapter-name-${ci}`} />
              <span className="text-sm tabular-nums font-bold text-primary hidden sm:inline">{eur(chTot.N)}</span>
              <span className="text-xs text-muted-foreground hidden md:inline">margen {chTot.N ? pct(chTot.R / chTot.N) : "—"}</span>
              {!ro && <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={() => delChapter(ci)}><Trash2 className="h-4 w-4" /></Button>}
            </div>

            {isOpen && (
              <div className="p-3 space-y-4">
                {ch.subchapters.map((sc, si) => {
                  const scTot = sumLines(sc.lines, effCfg);
                  return (
                    <div key={sc.id} className="rounded-lg border border-border" data-testid={`budget-sub-${ci}-${si}`}>
                      <div className="flex items-center gap-2 px-2.5 py-2 bg-muted/50 border-b border-border">
                        <span className="text-[10px] font-mono font-medium text-slate-600 bg-white border border-border rounded px-1.5 py-0.5">{String(ci + 1).padStart(2, "0")}.{String(si + 1).padStart(2, "0")}</span>
                        <input className="flex-1 bg-transparent text-sm font-semibold focus:outline-none focus:bg-white rounded px-1.5 py-0.5" value={sc.name} onChange={(e) => setSub(ci, si, { name: e.target.value })} data-testid={`sub-name-${ci}-${si}`} />
                        <span className="text-xs tabular-nums font-medium text-muted-foreground">{eur(scTot.N)}</span>
                        {!ro && <Button size="icon" variant="ghost" className="h-6 w-6 text-muted-foreground hover:text-destructive" onClick={() => delSub(ci, si)}><Trash2 className="h-3.5 w-3.5" /></Button>}
                      </div>

                      <div className="overflow-x-auto">
                        <table className="w-full text-sm min-w-[1360px]">
                          <thead>
                            <tr className="text-[10px] uppercase tracking-wider text-slate-500 text-left border-b border-border bg-muted/30">
                              <th className="px-2 py-2 font-semibold w-[22%]">Concepto</th>
                              <th className="px-2 py-2 font-semibold">Área</th>
                              <th className="px-2 py-2 font-semibold">In/Out</th>
                              <th className="px-2 py-2 font-semibold">Recurso</th>
                              <th className="px-2 py-2 font-semibold">Proveedor</th>
                              <th className="px-2 py-2 font-semibold w-14 text-right">Ctd</th>
                              <th className="px-2 py-2 font-semibold">Tipo</th>
                              <th className="px-2 py-2 font-semibold w-16 text-right">Uds</th>
                              <th className="px-2 py-2 font-semibold w-20 text-right">Coste €</th>
                              <th className="px-2 py-2 font-semibold text-right w-24">Precio con margen</th>
                              <th className="px-2 py-2 font-semibold text-right w-24">Precio cliente</th>
                              <th className="px-2 py-2 font-semibold text-right w-20">Margen previsto</th>
                              <th className="px-2 py-2 font-semibold text-right w-20">Margen real</th>
                              <th className="px-2 py-2"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {sc.lines.map((l, li) => {
                              const c = computeLine(l, effCfg);
                              const internal = l.in_out === "Interno";
                              const resOpts = internal ? (cfg.roles || []).map((r) => r.name) : EXT_RES;
                              const tipoOpts = internal ? TIME_TYPES : ["t/a", ...TIME_TYPES];
                              return (
                                <tr key={l.id} className={cn("border-b border-border/60 last:border-0 hover:bg-muted/30 transition-colors", internal ? "border-l-2 border-l-primary/50" : "border-l-2 border-l-navy/40")} data-testid={`budget-line-${ci}-${si}-${li}`}>
                                  <td className="p-1"><input className={cell} value={l.concepto} onChange={(e) => setLine(ci, si, li, { concepto: e.target.value })} placeholder="Concepto" data-testid={`line-concepto-${ci}-${si}-${li}`} /></td>
                                  <td className="p-1"><select className={cell} value={l.area} onChange={(e) => setLine(ci, si, li, { area: e.target.value })} data-testid={`line-area-${ci}-${si}-${li}`}>{(cfg.areas || []).map((a) => <option key={a} value={a}>{a}</option>)}</select></td>
                                  <td className="p-1"><select className={cn(cell, "font-medium", internal ? "text-primary" : "text-navy")} value={l.in_out} onChange={(e) => setLine(ci, si, li, { in_out: e.target.value })} data-testid={`line-inout-${ci}-${si}-${li}`}><option value="Interno">Interno</option><option value="Externo">Externo</option></select></td>
                                  <td className="p-1"><select className={cell} value={l.recurso} onChange={(e) => setLine(ci, si, li, { recurso: e.target.value })} data-testid={`line-recurso-${ci}-${si}-${li}`}>{resOpts.map((r) => <option key={r} value={r}>{r}</option>)}</select></td>
                                  <td className="p-1">{internal ? <span className="text-xs text-muted-foreground pl-2">—</span> : <SupplierCell line={l} ro={ro} suppliers={suppliers} onChange={(id, name) => setLine(ci, si, li, { supplier_id: id, supplier_name: name })} onSupplierCreated={(s) => setSuppliers((p) => [...p, s].sort((a, b) => (a.name || "").localeCompare(b.name || "")))} testid={`line-supplier-${ci}-${si}-${li}`} />}</td>
                                  <td className="p-1"><input type="number" className={cellNum} value={l.ctd} onChange={(e) => setLine(ci, si, li, { ctd: e.target.value })} data-testid={`line-ctd-${ci}-${si}-${li}`} /></td>
                                  <td className="p-1"><select className={cell} value={l.tipo} onChange={(e) => setLine(ci, si, li, { tipo: e.target.value })} data-testid={`line-tipo-${ci}-${si}-${li}`}>{tipoOpts.map((t) => <option key={t} value={t}>{t}</option>)}</select></td>
                                  <td className="p-1"><input type="number" className={cellNum} value={l.unidades} onChange={(e) => setLine(ci, si, li, { unidades: e.target.value })} data-testid={`line-uds-${ci}-${si}-${li}`} /></td>
                                  <td className="p-1"><input type="number" className={cn(cellNum, internal && "opacity-40")} value={internal ? Math.round(c.K * 100) / 100 : l.coste} disabled={internal} onChange={(e) => setLine(ci, si, li, { coste: e.target.value })} data-testid={`line-coste-${ci}-${si}-${li}`} /></td>
                                  <td className="p-1 text-right tabular-nums whitespace-nowrap text-muted-foreground" data-testid={`line-price-margin-${ci}-${si}-${li}`}>{eur(c.auto.N)}</td>
                                  <td className="p-1"><input type="number" className={cn(cellNum, "font-semibold")} value={(l.client_price_override !== undefined && l.client_price_override !== null && l.client_price_override !== "") ? l.client_price_override : Math.round(c.auto.N * 100) / 100} onChange={(e) => setLine(ci, si, li, { client_price_override: e.target.value })} data-testid={`line-price-client-${ci}-${si}-${li}`} /></td>
                                  <td className="p-1 text-right tabular-nums whitespace-nowrap text-muted-foreground" data-testid={`line-margin-prev-${ci}-${si}-${li}`}>{pct(c.auto.S)}</td>
                                  <td className={cn("p-1 text-right tabular-nums whitespace-nowrap font-medium", c.R >= 0 ? "text-primary" : "text-destructive")} data-testid={`line-margin-real-${ci}-${si}-${li}`}>{pct(c.S)}</td>
                                  <td className="p-1">{!ro && <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={() => delLine(ci, si, li)}><Trash2 className="h-3.5 w-3.5" /></Button>}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      <div className="px-2.5 py-1.5 border-t border-border flex items-center gap-1">
                        {!ro && <Button size="sm" variant="ghost" className="gap-1.5 h-7 text-muted-foreground" onClick={() => addLine(ci, si)} data-testid={`add-line-${ci}-${si}`}><Plus className="h-3.5 w-3.5" /> Añadir partida</Button>}
                        {canGlobal && <Button size="sm" variant="ghost" className="gap-1.5 h-7 text-muted-foreground" onClick={() => setTplTarget({ ci, si })} data-testid={`tpl-btn-${ci}-${si}`}><LayoutTemplate className="h-3.5 w-3.5" /> Plantillas</Button>}
                      </div>
                    </div>
                  );
                })}
                {!ro && <Button size="sm" variant="outline" className="gap-1.5" onClick={() => addSub(ci)} data-testid={`add-sub-${ci}`}><Plus className="h-4 w-4" /> Añadir subcapítulo</Button>}
              </div>
            )}
          </Card>
        );
      })}

      {chapters.length > 0 && !ro && (
        <Button variant="outline" className="gap-1.5" onClick={addChapter} data-testid="budget-add-chapter"><FolderPlus className="h-4 w-4" /> Añadir capítulo</Button>
      )}

      {chapters.length > 0 && (
        <div className="flex justify-end pt-1">
          <div className="rounded-xl border border-border bg-muted/30 px-5 py-3 flex items-center gap-6 flex-wrap" data-testid="budget-grand-total-bar">
            <div className="text-right">
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Total presupuesto (precio cliente)</div>
              <div className="font-heading font-bold text-2xl tabular-nums text-navy" data-testid="budget-grand-total">{eur(grand.N)}</div>
            </div>
            <div className="hidden sm:block h-10 w-px bg-border" />
            <div className="text-right">
              <div className="text-[11px] text-muted-foreground">Coste total</div>
              <div className="font-semibold tabular-nums" data-testid="budget-grand-cost">{eur(grand.O + grand.P)}</div>
            </div>
            <div className="text-right">
              <div className="text-[11px] text-muted-foreground">Margen neto</div>
              <div className={cn("font-semibold tabular-nums", grand.R >= 0 ? "text-primary" : "text-destructive")} data-testid="budget-grand-margin">{eur(grand.R)} <span className="text-[11px] text-muted-foreground">{grand.N ? pct(grand.R / grand.N) : ""}</span></div>
            </div>
          </div>
        </div>
      )}

      </fieldset>

      {showConfig && <ConfigDialog open={showConfig} onOpenChange={setShowConfig} cfg={cfg} onSaved={(nc) => setCfg({ ...cfg, ...nc })} />}
      {showCopy && <CopyDialog open={showCopy} onOpenChange={setShowCopy} currentPid={projectId} onCopy={copyFromProject} />}
      {showImport && <ImportExcelDialog open={showImport} onOpenChange={setShowImport} projectId={projectId}
        hasCurrent={chapters.some((c) => (c.subchapters || []).some((s) => (s.lines || []).length > 0))}
        onApplied={() => { loadVersions(); reloadBudget(); }} />}
      {tplTarget && (
        <TemplatesDialog
          open={!!tplTarget}
          onOpenChange={(v) => { if (!v) setTplTarget(null); }}
          templates={templates}
          currentLines={chapters[tplTarget.ci]?.subchapters[tplTarget.si]?.lines || []}
          onInsert={(lines) => insertTemplate(tplTarget.ci, tplTarget.si, lines)}
          onSave={saveTemplate}
          onDelete={deleteTemplate}
        />
      )}
    </div>
  );
}
